import { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { useNavigate } from 'react-router-dom';
import { useRecordNavigator } from '../../hooks/useRecordNavigator';
import { emailHitTarget, smartSearchBackend, type SmartEntity, type SmartHit, type SmartRecent } from '../../utils/smartSearch';

const SEARCH_DEBOUNCE_MS = 150;

interface Arrived {
  seq: number;
  scope: string;
  query: string;
  hits: SmartHit[];
}

/**
 * Debounced smart search. While the current query is on its way it shows the
 * newest answer that has come back for a prefix of it (or a longer form of it),
 * so typing "truck" shows "t", "tr", ... as they land instead of an empty box
 * until typing stops. An answer for an unrelated earlier search is never shown.
 */
export function useSmartSearch(query: string, entities: SmartEntity[] | null, limit = 8, enabled = true) {
  const debounced = useDebounce(query.trim(), SEARCH_DEBOUNCE_MS);
  const scope = `${entities?.join(',') ?? 'all'}:${limit}`;
  const sent = useRef(0);
  const [arrived, setArrived] = useState<Arrived | null>(null);
  const result = useQuery({
    queryKey: ['smart-search', entities?.join(',') ?? 'all', limit, debounced.toLowerCase()],
    queryFn: async () => {
      const seq = ++sent.current;
      const hits = await smartSearchBackend.search(debounced, entities, limit);
      // Answers can land out of order; keep the newest request's.
      setArrived((a) => (a && a.seq > seq ? a : { seq, scope, query: debounced.toLowerCase(), hits }));
      return hits;
    },
    enabled: enabled && debounced.length > 0,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
  const typed = query.trim().toLowerCase();
  const related = arrived !== null && arrived.scope === scope && (typed.startsWith(arrived.query) || arrived.query.startsWith(typed));
  const settled = debounced === query.trim();
  const hits: SmartHit[] = typed.length > 0 && enabled ? result.data ?? (related ? arrived.hits : []) : [];
  return { hits, isFetching: result.isFetching || !settled, error: result.error };
}

export function useSmartRecent(enabled: boolean, limit = 6) {
  const result = useQuery({
    queryKey: ['smart-search-recent', limit],
    queryFn: () => smartSearchBackend.recent(limit),
    enabled,
    staleTime: 15_000,
  });
  return result.data ?? [];
}

type Openable = Pick<SmartHit, 'entity' | 'entity_id' | 'merchant_id' | 'title' | 'submission_number'>
  & Partial<Pick<SmartHit, 'email_source' | 'email_thread' | 'mailbox' | 'sender'>>;

/** Open a hit the way the rest of the app opens that record, and remember the choice. */
export function useOpenSmartHit() {
  const { openRecord } = useRecordNavigator();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useCallback((hit: Openable | SmartRecent, query: string) => {
    // Leads open in the Imports lead drawer. search_selection doesn't take
    // leads, so they don't rank by opens or show in Recent.
    if (hit.entity === 'lead') {
      navigate(`/Imports?tab=leads&lead=${hit.entity_id}`);
      return;
    }
    // Emails (REG-885) open where the app shows them; like leads they are
    // not ranked by opens or kept in Recent.
    if (hit.entity === 'email') {
      const target = emailHitTarget(hit as Openable);
      if (target.kind === 'merchant') {
        openRecord({ tableName: 'merchant', id: target.merchantId, label: `Merchant #${target.merchantId}` });
      } else {
        navigate(target.path);
      }
      return;
    }
    // What a user opens ranks higher for them next time and shows in Recent.
    void smartSearchBackend.recordSelection(query, hit)
      .then(() => queryClient.invalidateQueries({ queryKey: ['smart-search-recent'] }))
      .then(() => queryClient.invalidateQueries({ queryKey: ['smart-search'] }))
      .catch((error) => console.error('smart search selection', error));
    if ((hit.entity === 'merchant' || hit.entity === 'deal') && hit.merchant_id != null) {
      openRecord({
        tableName: 'merchant',
        id: hit.merchant_id,
        label: hit.title || `Merchant #${hit.merchant_id}`,
        ...(hit.entity === 'deal' ? { tab: 'Submissions' } : {}),
      });
      return;
    }
    if (hit.entity === 'deal') {
      openRecord({
        tableName: 'submission',
        id: hit.entity_id,
        displayId: hit.submission_number ?? hit.entity_id,
        label: `Submission #${hit.submission_number ?? hit.entity_id}`,
      });
      return;
    }
    openRecord({ tableName: hit.entity, id: hit.entity_id, label: hit.title });
  }, [navigate, openRecord, queryClient]);
}
